import { useCallback, useEffect, useState } from 'react'
import { Check, Gift, Loader2, ArrowUpRight } from 'lucide-react'
import clsx from 'clsx'
import {
  fetchOnboardingProgress,
  claimOnboardingTask,
  type OnboardingProgress,
  type OnboardingTask,
} from '@/lib/data'
import { trackEvent } from '@/lib/posthog'

/**
 * "Let's get you started" rewards checklist, the same growth loop Firecrawl
 * runs on signup: complete quick actions, earn bonus AI credits. Activation
 * tasks unlock once the user has actually done the thing; social tasks open a
 * link and are claimable right away.
 */
export function OnboardingRewards({
  className,
  onBalanceChange,
}: {
  className?: string
  onBalanceChange?: (balance: number) => void
}) {
  const [data, setData] = useState<OnboardingProgress | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [claiming, setClaiming] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const p = await fetchOnboardingProgress()
      setData(p)
      onBalanceChange?.(p.balance)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [onBalanceChange])

  useEffect(() => {
    void load()
  }, [load])

  const claim = async (task: OnboardingTask) => {
    if (claiming || task.claimed || !task.completed) return
    setClaiming(task.key)
    try {
      const res = await claimOnboardingTask(task.key)
      trackEvent('onboarding_reward_claimed', {
        task: task.key,
        kind: task.kind,
        credits: res.creditsAwarded,
      })
      onBalanceChange?.(res.balance)
      await load()
    } catch {
      // Re-sync so the UI reflects real state (e.g. a task no longer eligible).
      await load()
    } finally {
      setClaiming(null)
    }
  }

  if (loading) {
    return (
      <div className={clsx('grid place-items-center py-10', className)}>
        <Loader2 className="animate-spin text-brand-400" size={24} />
      </div>
    )
  }

  if (error || !data) return null

  const pct = data.totalCount ? Math.round((data.claimedCount / data.totalCount) * 100) : 0

  return (
    <div className={className}>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-ink-900">
            Let's get you started
          </h2>
          <p className="mt-1 text-sm text-ink-500">
            Complete these quick actions to earn bonus AI credits.
          </p>
        </div>
        <span className="chip-info chip-dot">
          {data.balance} credits
        </span>
      </div>

      {/* progress */}
      <div className="mb-6 flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-panel">
          <div
            className="h-full rounded-full bg-brand-500 transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="whitespace-nowrap text-xs font-semibold text-ink-500">
          {data.claimedCount} of {data.totalCount}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.tasks.map((t) => (
          <TaskCard
            key={t.key}
            task={t}
            claiming={claiming === t.key}
            onClaim={() => void claim(t)}
          />
        ))}
      </div>
    </div>
  )
}

function TaskCard({
  task,
  claiming,
  onClaim,
}: {
  task: OnboardingTask
  claiming: boolean
  onClaim: () => void
}) {
  const claimed = task.claimed
  return (
    <div
      className={clsx(
        'flex flex-col rounded-2xl border p-4 transition-colors',
        claimed ? 'border-accent-green/40 bg-accent-green/5' : 'border-line bg-surface',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink-900">{task.title}</h3>
        <span
          className={clsx(
            'shrink-0 rounded-lg px-2 py-0.5 text-xs font-bold',
            claimed ? 'bg-accent-green/10 text-[#1e8e43]' : 'bg-brand-50 text-brand-700',
          )}
        >
          +{task.credits}
        </span>
      </div>
      <p className="mt-1 text-[13px] leading-snug text-ink-500">{task.description}</p>

      <div className="mt-4 pt-1">
        {claimed ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1e8e43]">
            <Check size={14} /> Claimed
          </span>
        ) : task.kind === 'social' && task.url ? (
          <a
            href={task.url}
            target="_blank"
            rel="noreferrer"
            onClick={onClaim}
            className="btn-ghost w-full px-3 py-2 text-xs"
          >
            {claiming ? (
              <Loader2 className="animate-spin" size={14} />
            ) : (
              <>
                <Gift size={14} /> Claim <ArrowUpRight size={14} />
              </>
            )}
          </a>
        ) : task.completed ? (
          <button
            type="button"
            onClick={onClaim}
            disabled={claiming}
            className="btn-primary w-full px-3 py-2 text-xs"
          >
            {claiming ? <Loader2 className="animate-spin" size={14} /> : (<><Gift size={14} /> Claim</>)}
          </button>
        ) : (
          <span className="chip-pending">Not done yet</span>
        )}
      </div>
    </div>
  )
}
